// Format helpers for structured / repeater values stored as JSON in db.profil.
// Used by read-only views (HeritierView, Couple, Partages, etc.).

export function tryParseStructured(value) {
  if (value === null || value === undefined) return { kind: "empty" };
  if (typeof value !== "string") return { kind: "raw", value: String(value) };
  const trimmed = value.trim();
  if (!trimmed) return { kind: "empty" };
  if (trimmed.startsWith("[") || trimmed.startsWith("{")) {
    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) return { kind: "list", items: parsed };
      if (parsed && typeof parsed === "object") return { kind: "object", obj: parsed };
    } catch (_e) {
      return { kind: "raw", value: trimmed };
    }
  }
  return { kind: "raw", value: trimmed };
}

function humanizeKey(k) {
  return String(k).replace(/_/g, " ");
}

export function formatStructuredValue(value) {
  const parsed = tryParseStructured(value);
  if (parsed.kind === "empty") return "";
  if (parsed.kind === "raw") return parsed.value;
  if (parsed.kind === "object") {
    const lines = Object.entries(parsed.obj)
      .filter(([, v]) => v && String(v).trim())
      .map(([k, v]) => `${humanizeKey(k)}: ${v}`);
    return lines.join(" · ");
  }
  if (parsed.kind === "list") {
    if (parsed.items.length === 0) return "";
    return parsed.items
      .map((it, idx) => {
        if (!it || typeof it !== "object") return `${idx + 1}. ${it}`;
        const parts = Object.entries(it)
          .filter(([, v]) => v && String(v).trim())
          .map(([k, v]) => `${humanizeKey(k)}: ${v}`);
        return `${idx + 1}. ${parts.join(" · ") || "(vide)"}`;
      })
      .join("\n");
  }
  return "";
}

// Pretty React renderer for read-only views (returns JSX)
import React from "react";

export function RenderStructuredValue({ value }) {
  const parsed = tryParseStructured(value);
  if (parsed.kind === "empty") return null;
  if (parsed.kind === "raw") {
    return <span style={{ whiteSpace: "pre-wrap" }}>{parsed.value}</span>;
  }
  if (parsed.kind === "object") {
    return (
      <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", columnGap: 10, rowGap: 4 }}>
        {Object.entries(parsed.obj)
          .filter(([, v]) => v && String(v).trim())
          .map(([k, v]) => (
            <React.Fragment key={k}>
              <span style={{ fontSize: 12, color: "rgba(35,38,46,0.55)", textTransform: "capitalize" }}>{humanizeKey(k)}</span>
              <span style={{ fontSize: 14 }}>{v}</span>
            </React.Fragment>
          ))}
      </div>
    );
  }
  if (parsed.kind === "list") {
    if (parsed.items.length === 0) return null;
    return (
      <ul style={{ paddingLeft: 16, margin: 0, lineHeight: 1.7 }}>
        {parsed.items.map((it, idx) => {
          if (!it || typeof it !== "object") {
            return <li key={idx}>{String(it)}</li>;
          }
          const parts = Object.entries(it)
            .filter(([, v]) => v && String(v).trim())
            .map(([k, v]) => (
              <span key={k}>
                <span style={{ color: "rgba(35,38,46,0.55)", fontSize: 12, textTransform: "capitalize", marginRight: 4 }}>{humanizeKey(k)}:</span>
                <span style={{ fontSize: 14 }}>{v}</span>
              </span>
            ));
          return (
            <li key={idx} style={{ marginBottom: 4 }}>
              {parts.length === 0 ? "(vide)" : parts.reduce((acc, p, i) => acc.concat(i === 0 ? [p] : [<span key={`sep-${i}`} style={{ margin: "0 6px", color: "rgba(35,38,46,0.3)" }}>·</span>, p]), [])}
            </li>
          );
        })}
      </ul>
    );
  }
  return null;
}
